import React, { useRef, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Switch } from "../components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Check, UploadSimple, FileText, YoutubeLogo, TiktokLogo, LinkSimple } from "@phosphor-icons/react";
import { Facebook } from "lucide-react";
import { uploadScript, uploadToDrive } from "../lib/api";
import { toast } from "sonner";

const STAGES = ["idea", "script", "filming", "editing", "review", "ready", "scheduled", "published"];

export function VideoEditForm({ open, onOpenChange, formData, setFormData, onSubmit, editingVideo, editors }) {
  const [scriptUploading, setScriptUploading] = useState(false);
  const [driveUploading, setDriveUploading] = useState(false);
  const [driveUploadResult, setDriveUploadResult] = useState(null);
  const scriptInputRef = useRef(null);
  const driveVideoInputRef = useRef(null);

  const handleScriptUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setScriptUploading(true);
    try {
      const response = await uploadScript(file);
      setFormData((prev) => ({ ...prev, script_url: response.data.url }));
      toast.success(`Script "${file.name}" uploaded`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Script upload failed");
    } finally {
      setScriptUploading(false);
      if (scriptInputRef.current) scriptInputRef.current.value = "";
    }
  };

  const handleDriveVideoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setDriveUploading(true);
    setDriveUploadResult(null);
    try {
      const folderId = formData.drive_folder_url
        ? formData.drive_folder_url.match(/folders\/([a-zA-Z0-9_-]+)/)?.[1] || null
        : null;
      const response = await uploadToDrive(file, folderId);
      setDriveUploadResult(response.data);
      toast.success(`Video "${file.name}" uploaded to Google Drive`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Drive upload failed");
    } finally {
      setDriveUploading(false);
      if (driveVideoInputRef.current) driveVideoInputRef.current.value = "";
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#1A1A2E] border-white/10 max-w-xl">
        <DialogHeader>
          <DialogTitle className="text-white">
            {editingVideo ? "Edit Video" : "Create Video"}
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={onSubmit} className="space-y-4 max-h-[70vh] overflow-y-auto pr-1">
          <div className="space-y-2">
            <Label className="text-gray-300">Title</Label>
            <Input
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              className="bg-white/5 border-white/10 text-white"
              placeholder="Video title"
              required
              data-testid="video-title-input"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="text-gray-300">Type</Label>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => setFormData({ ...formData, is_long: !formData.is_long })}
                  className={`flex-1 flex items-center justify-center gap-2 px-3 py-2 rounded-lg border text-sm font-medium transition-all ${
                    formData.is_long
                      ? "bg-blue-500/15 border-blue-500/40 text-blue-400"
                      : "bg-white/5 border-white/10 text-gray-500"
                  }`}
                  data-testid="type-long-toggle"
                >
                  {formData.is_long && <Check size={14} weight="bold" />}
                  Long
                </button>
                <button
                  type="button"
                  onClick={() => setFormData({ ...formData, is_short: !formData.is_short })}
                  className={`flex-1 flex items-center justify-center gap-2 px-3 py-2 rounded-lg border text-sm font-medium transition-all ${
                    formData.is_short
                      ? "bg-purple-500/15 border-purple-500/40 text-purple-400"
                      : "bg-white/5 border-white/10 text-gray-500"
                  }`}
                  data-testid="type-short-toggle"
                >
                  {formData.is_short && <Check size={14} weight="bold" />}
                  Short
                </button>
              </div>
              {formData.is_short && (
                <div className="flex items-center gap-2 mt-1">
                  <Label className="text-gray-400 text-xs whitespace-nowrap">No. of Shorts</Label>
                  <Input
                    type="number"
                    min="0"
                    value={formData.shorts_count}
                    onChange={(e) => setFormData({ ...formData, shorts_count: parseInt(e.target.value) || 0 })}
                    className="bg-white/5 border-white/10 text-white w-20 h-8 text-sm"
                    data-testid="shorts-count-input"
                  />
                </div>
              )}
            </div>

            <div className="space-y-2">
              <Label className="text-gray-300">Stage</Label>
              <Select value={formData.stage} onValueChange={(v) => setFormData({ ...formData, stage: v })}>
                <SelectTrigger className="bg-white/5 border-white/10 text-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#1A1A2E] border-white/10">
                  {STAGES.map((s) => (
                    <SelectItem key={s} value={s} className="text-white capitalize">{s}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="text-gray-300">Priority (1-3)</Label>
              <select
                value={formData.priority ?? ""}
                onChange={(e) => setFormData({ ...formData, priority: e.target.value ? parseInt(e.target.value) : null })}
                className="w-full h-10 rounded-md bg-white/5 border border-white/10 text-white px-3 text-sm"
              >
                <option value="">None</option>
                <option value="1">1 (Top)</option>
                <option value="2">2</option>
                <option value="3">3</option>
              </select>
            </div>

            <div className="space-y-2">
              <Label className="text-gray-300">Editor</Label>
              <Select value={formData.editor_id || "none"} onValueChange={(v) => setFormData({ ...formData, editor_id: v === "none" ? "" : v })}>
                <SelectTrigger className="bg-white/5 border-white/10 text-white">
                  <SelectValue placeholder="Select editor" />
                </SelectTrigger>
                <SelectContent className="bg-[#1A1A2E] border-white/10">
                  <SelectItem value="none" className="text-gray-400">Unassigned</SelectItem>
                  {editors.map((e) => (
                    <SelectItem key={e.id} value={e.id} className="text-white">{e.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-2">
            <Label className="text-gray-300">Scheduled Date</Label>
            <Input
              type="date"
              value={formData.scheduled_date}
              onChange={(e) => setFormData({ ...formData, scheduled_date: e.target.value })}
              className="bg-white/5 border-white/10 text-white"
              data-testid="video-scheduled-date-input"
            />
          </div>

          {/* Short Required Toggle */}
          <div className="flex items-center justify-between p-3 rounded-lg bg-white/5 border border-white/10">
            <div>
              <Label className="text-gray-200 font-medium">Short Required</Label>
              <p className="text-xs text-gray-500 mt-0.5">Does this video need a short version?</p>
            </div>
            <Switch
              checked={formData.short_required}
              onCheckedChange={(v) => setFormData({ ...formData, short_required: v })}
              data-testid="short-required-toggle"
            />
          </div>

          {/* Platform Upload Tracking */}
          <div className="space-y-2">
            <Label className="text-gray-300">Platform Upload Status</Label>
            <div className="grid grid-cols-3 gap-2">
              <button
                type="button"
                onClick={() => setFormData({ ...formData, uploaded_youtube: !formData.uploaded_youtube })}
                className={`flex items-center justify-center gap-1.5 p-2.5 rounded-lg border text-sm font-medium transition-all ${
                  formData.uploaded_youtube
                    ? "bg-red-500/15 border-red-500/40 text-red-400"
                    : "bg-white/5 border-white/10 text-gray-500"
                }`}
                data-testid="toggle-youtube"
              >
                <YoutubeLogo size={16} weight="fill" />
                <span>{formData.uploaded_youtube ? "\u2705" : "\u23F3"}</span>
              </button>
              <button
                type="button"
                onClick={() => setFormData({ ...formData, uploaded_facebook: !formData.uploaded_facebook })}
                className={`flex items-center justify-center gap-1.5 p-2.5 rounded-lg border text-sm font-medium transition-all ${
                  formData.uploaded_facebook
                    ? "bg-blue-500/15 border-blue-500/40 text-blue-400"
                    : "bg-white/5 border-white/10 text-gray-500"
                }`}
                data-testid="toggle-facebook"
              >
                <Facebook size={16} />
                <span>{formData.uploaded_facebook ? "\u2705" : "\u23F3"}</span>
              </button>
              <button
                type="button"
                onClick={() => setFormData({ ...formData, uploaded_tiktok: !formData.uploaded_tiktok })}
                className={`flex items-center justify-center gap-1.5 p-2.5 rounded-lg border text-sm font-medium transition-all ${
                  formData.uploaded_tiktok
                    ? "bg-white/15 border-white/40 text-white"
                    : "bg-white/5 border-white/10 text-gray-500"
                }`}
                data-testid="toggle-tiktok"
              >
                <TiktokLogo size={16} weight="fill" />
                <span>{formData.uploaded_tiktok ? "\u2705" : "\u23F3"}</span>
              </button>
            </div>
          </div>

          {/* Script Upload */}
          <div className="space-y-2">
            <Label className="text-gray-300">Script File</Label>
            <div className="flex items-center gap-2">
              <input
                ref={scriptInputRef}
                type="file"
                accept=".docx,.pdf,.txt"
                onChange={handleScriptUpload}
                className="hidden"
                data-testid="script-file-input"
              />
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => scriptInputRef.current?.click()}
                disabled={scriptUploading}
                className="btn-secondary text-xs"
                data-testid="script-upload-btn"
              >
                <UploadSimple size={14} className="mr-1" />
                {scriptUploading ? "Uploading..." : "Upload Script"}
              </Button>
              {formData.script_url && (
                <a
                  href={`${process.env.REACT_APP_BACKEND_URL}${formData.script_url}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-xs text-cyan-400 hover:text-cyan-300 flex items-center gap-1"
                  data-testid="script-download-link"
                >
                  <FileText size={14} /> Download Script
                </a>
              )}
            </div>
          </div>

          {/* Google Drive Folder URL */}
          <div className="space-y-2">
            <Label className="text-gray-300">Google Drive Folder URL</Label>
            <div className="flex items-center gap-2">
              <LinkSimple size={16} className="text-gray-500 shrink-0" />
              <Input
                value={formData.drive_folder_url}
                onChange={(e) => setFormData({ ...formData, drive_folder_url: e.target.value })}
                className="bg-white/5 border-white/10 text-white"
                placeholder="https://drive.google.com/drive/folders/..."
                data-testid="drive-folder-url-input"
              />
            </div>
          </div>

          {/* Upload Video to Google Drive */}
          <div className="space-y-2">
            <Label className="text-gray-300">Upload Video to Drive</Label>
            <div className="flex items-center gap-2 flex-wrap">
              <input
                ref={driveVideoInputRef}
                type="file"
                accept="video/*,.mp4,.mov,.avi,.mkv,.webm"
                onChange={handleDriveVideoUpload}
                className="hidden"
                data-testid="drive-video-file-input"
              />
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => driveVideoInputRef.current?.click()}
                disabled={driveUploading}
                className="btn-secondary text-xs"
                data-testid="drive-video-upload-btn"
              >
                <UploadSimple size={14} className="mr-1" />
                {driveUploading ? "Uploading to Drive..." : "Upload Video"}
              </Button>
              {driveUploadResult && (
                <a
                  href={driveUploadResult.web_link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-xs text-green-400 hover:text-green-300 flex items-center gap-1"
                  data-testid="drive-video-link"
                >
                  <Check size={14} /> {driveUploadResult.name}
                </a>
              )}
            </div>
            {!formData.drive_folder_url && (
              <p className="text-[11px] text-gray-500">Add a Drive folder URL above to upload into a specific folder.</p>
            )}
          </div>

          <div className="space-y-2">
            <Label className="text-gray-300">Notes</Label>
            <Textarea
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              className="bg-white/5 border-white/10 text-white min-h-[80px]"
              placeholder="Any notes about this video..."
              data-testid="video-notes-input"
            />
          </div>

          <div className="flex gap-3 pt-4">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} className="flex-1 btn-secondary">
              Cancel
            </Button>
            <Button type="submit" className="flex-1 btn-primary" data-testid="video-submit-btn">
              {editingVideo ? "Save Changes" : "Create Video"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
